import { Popover } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTour } from '../../contexts/TourContext';
import Button from '../Button/Button';
import './ContactsTour.scss';

const ContactsTour = ({ instructionsRead = false, onFinish }) => {
  const history = useHistory();
  const { tourStep, setStep, stopTour, finishTour } = useTour();
  const [currentScreen, setCurrentScreen] = useState('table');
  const [timeLeft, setTimeLeft] = useState(10);
  const [canProceed, setCanProceed] = useState(false);
  const [isReady, setIsReady] = useState(true); // Флаг готовности для отрисовки
  const prevScreenRef = useRef('table'); // Отслеживание предыдущего экрана

  // Определяем текущий экран по URL
  useEffect(() => {
    const path = history.location.pathname;
    if (path === '/contacts/create') {
      setCurrentScreen('create');
    } else if (path.startsWith('/contacts/edit/')) {
      setCurrentScreen('manage');
    } else {
      setCurrentScreen('table');
    }
  }, [history.location.pathname]);

  // Задержка отрисовки ТОЛЬКО при смене экрана (table → create или наоборот)
  useEffect(() => {
    // Проверяем, действительно ли изменился экран
    if (prevScreenRef.current !== currentScreen) {
      setIsReady(false);
      const timer = setTimeout(() => {
        setIsReady(true);
        prevScreenRef.current = currentScreen; // Обновляем ref после завершения
      }, 700);

      return () => clearTimeout(timer);
    }
  }, [currentScreen]);

  // Таймер на 10 секунд для каждого шага (ТОЛЬКО если еще не читали инструкцию)
  useEffect(() => {
    if (!instructionsRead) {
      setTimeLeft(10);
      setCanProceed(false);

      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setCanProceed(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      // Если уже читали - сразу разрешаем переход
      setCanProceed(true);
      setTimeLeft(0);
    }
  }, [tourStep, instructionsRead]);

  // Автоскролл вниз при переходе на экран создания
  useEffect(() => {
    if (currentScreen === 'create' && tourStep >= 5) {
      // Сначала прокручиваем
      setTimeout(() => {
        const pageContent = document.querySelector('.page__content');
        const contactsCreatePage = document.querySelector('.contacts-page');

        if (pageContent) {
          pageContent.scrollTo({
            top: pageContent.scrollHeight,
            behavior: 'smooth',
          });
        } else if (contactsCreatePage) {
          contactsCreatePage.scrollTo({
            top: contactsCreatePage.scrollHeight,
            behavior: 'smooth',
          });
        } else {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 300);
    }
  }, [currentScreen, tourStep]);

  // ПОЛНЫЙ ТУР - все шаги от начала до конца
  const allSteps = [
    // ============================================
    // ЧАСТЬ 1: ТАБЛИЦА СПІВРОБІТНИКІВ (5 шагов)
    // ============================================
    {
      title: 'Вітаємо! Почнемо з таблиці',
      target: '.contacts-clickable-rows',
      screen: 'table',
      content: (
        <div>
          <p>
            <strong>Це основна таблиця</strong> з усіма співробітниками вашого магазину.
          </p>
          <p>📋 Тут ви можете побачити:</p>
          <ul>
            <li>
              <strong>Ім'я співробітника</strong> та його посаду
            </li>
            <li>
              <strong>Номер телефону</strong> у форматі +38 (0XX) XXX-XX-XX
            </li>
            <li>
              <strong>Номер картки</strong> (якщо призначена)
            </li>
            <li>
              <strong>Статус картки</strong> співробітника
            </li>
          </ul>
          <p>💡 Натисніть на будь-який рядок, щоб перейти до управління картками.</p>
        </div>
      ),
      placement: 'top',
    },
    {
      title: 'Статуси карток - важливо!',
      target: '.contacts-clickable-rows tbody tr:first-child td.contact-card-status-column',
      fallbackTarget: '.contacts-clickable-rows',
      screen: 'table',
      content: (
        <div>
          <p>
            <strong>Картка може мати 4 типи статусів:</strong>
          </p>
          <ul>
            <li>
              <span style={{ color: '#8c8c8c', fontWeight: 600 }}>🔘 Не призначено</span> — у
              співробітника ще немає картки
            </li>
            <li>
              <span style={{ color: '#DB3EB1', fontWeight: 600 }}>🟣 Очікуйте</span> — картка
              виготовляється (заявка подана, чекаємо випуск)
            </li>
            <li>
              <span style={{ color: '#1890ff', fontWeight: 600 }}>🔵 Готова до видачі</span> — картка готова
              та може бути видана співробітнику
            </li>
            <li>
              <span style={{ color: '#52c41a', fontWeight: 600 }}>🟢 Видана</span> — картка вже у
              співробітника, він може нею користуватися
            </li>
          </ul>
          <p>
            ⚠️ <strong>Заблокована</strong> картка не відображається у таблиці.
          </p>
          <p
            style={{
              marginTop: '12px',
              padding: '8px',
              background: '#fef3c7',
              borderRadius: '6px',
            }}
          >
            💡 Якщо таблиця порожня, додайте співробітника на наступному кроці!
          </p>
        </div>
      ),
      placement: 'left',
    },
    {
      title: 'Лічильник доступних карток',
      target: '.cards-available',
      screen: 'table',
      content: (
        <div>
          <p>
            <strong>Цей лічильник показує:</strong>
          </p>
          <ul>
            <li>
              <span style={{ color: '#DB3EB1', fontWeight: 600 }}>Рожеве число</span> — кількість
              випущених карток, які можна призначити співробітникам прямо зараз
            </li>
            <li>
              <strong>Загальне число</strong> — всі картки у системі (окрім заблокованих)
            </li>
          </ul>
          <p>
            💡 Якщо рожевих карток <strong>0</strong>, ви можете скористатися кнопкою <strong>«Замовити СКД»</strong> у цій же таблиці для виготовлення нових карток.
          </p>
        </div>
      ),
      placement: 'left',
    },
    {
      title: 'Швидкий пошук',
      target: '.search-input',
      screen: 'table',
      content: (
        <div>
          <p>
            <strong>Швидкий пошук</strong> допоможе знайти потрібного співробітника:
          </p>
          <ul>
            <li>За ім'ям або прізвищем</li>
            <li>За номером телефону</li>
            <li>За посадою</li>
            <li>За номером картки</li>
          </ul>
          <p>🔍 Просто почніть вводити текст — таблиця відфільтрується автоматично.</p>
        </div>
      ),
      placement: 'bottom',
    },
    {
      title: 'Масове замовлення карток СКД',
      target: '.order-skd-btn',
      screen: 'table',
      content: (
        <div>
          <p>
            <strong>Кнопка «Замовити СКД»</strong> дозволяє створити 1 пакетне звернення для декількох співробітників:
          </p>
          <ul>
            <li>Натисніть кнопку — у таблиці з'являться чекбокси.</li>
            <li>Верхній чекбокс автоматично обирає всіх співробітників без картки та без активного замовлення.</li>
            <li>Натисніть <strong>«Підтвердити»</strong> для створення одного спільного звернення на виготовлення.</li>
          </ul>
        </div>
      ),
      placement: 'bottom',
    },
    {
      title: 'Готові додати співробітника?',
      target: '.add-employee-btn',
      screen: 'table',
      content: (
        <div>
          <p>
            <strong>Натисніть цю кнопку</strong>, щоб додати нового співробітника до системи.
          </p>
          <p>📝 Ви перейдете на форму створення співробітника.</p>
          <p>
            💡 <strong>На наступному кроці</strong> ми детально розглянемо, як заповнити форму
            створення!
          </p>
          <p
            style={{
              marginTop: '12px',
              padding: '8px',
              background: '#f0f9ff',
              borderRadius: '6px',
            }}
          >
            ℹ️ Зараз просто продовжуйте інструкцію, не натискайте кнопку. Ми автоматично перейдемо
            на потрібну сторінку.
          </p>
        </div>
      ),
      placement: 'bottom',
      autoNavigate: '/contacts/create',
    },

    // ============================================
    // ЧАСТЬ 2: СТВОРЕННЯ СПІВРОБІТНИКА (4 шага)
    // ============================================
    {
      title: 'Форма створення співробітника',
      target: '.form.field-grey-straight-border',
      screen: 'create',
      content: (
        <div>
          <p>
            <strong>⚠️ Всі поля форми обов'язкові!</strong>
          </p>
          <p>Без заповнення всіх даних не можна створити співробітника.</p>
          <p>📋 Що потрібно заповнити:</p>
          <ul>
            <li>
              <strong>Ім'я</strong> — ім'я співробітника
            </li>
            <li>
              <strong>Прізвище</strong> — прізвище співробітника
            </li>
            <li>
              <strong>Номер телефону</strong> — у форматі +380XXXXXXXXX
            </li>
            <li>
              <strong>Email</strong> — електронна пошта
            </li>
            <li>
              <strong>Фото</strong> — фотографія співробітника
            </li>
            <li>
              <strong>Посада</strong> — посада співробітника
            </li>
          </ul>
          <p>💡 Давайте розглянемо кожне поле детальніше!</p>
        </div>
      ),
      placement: 'right',
    },
    {
      title: 'Номер телефону - формат важливий!',
      target: '.contact-phone-input',
      screen: 'create',
      content: (
        <div>
          <p>
            <strong>Формат номера:</strong> +380XXXXXXXXX
          </p>
          <p>📱 Вимоги до номера:</p>
          <ul>
            <li>Повинен починатися з +38</li>
            <li>Містити рівно 12 цифр (разом з +38)</li>
            <li>
              Мати дійсний код оператора:{' '}
              <strong>050, 066, 067, 068, 093, 095, 096, 097, 098, 099</strong>
            </li>
          </ul>
          <p>
            ⚠️ Система автоматично перевіряє, чи не використовується цей номер іншим співробітником.
          </p>
          <p style={{ marginTop: '8px', color: '#059669', fontWeight: 600 }}>
            ✅ Приклад: +380501234567
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      title: 'Завантаження фото співробітника',
      target: '.contact-photo-upload',
      screen: 'create',
      content: (
        <div>
          <p>
            <strong>Завантажте фото співробітника</strong> у форматі JPG або PNG.
          </p>
          <p>📸 Вимоги до фото:</p>
          <ul>
            <li>
              <strong>Формат:</strong> JPG або PNG
            </li>
            <li>Бажано портретне фото (обличчя)</li>
            <li>Чітке зображення</li>
            <li>Гарна освітленість</li>
          </ul>
          <p>
            💡 Це фото буде використовуватися для ідентифікації співробітника у системі та на картці
            СКД.
          </p>
        </div>
      ),
      placement: 'left',
    },
    {
      title: 'Створення співробітника',
      target: '.btn-create-contact',
      screen: 'create',
      content: (
        <div>
          <p>
            <strong>Після заповнення всіх полів</strong> натисніть кнопку "Створити".
          </p>
          <p>✅ Що станеться далі:</p>
          <ul>
            <li>Співробітник буде доданий до системи</li>
            <li>
              Статус картки буде{' '}
              <span style={{ color: '#8c8c8c', fontWeight: 600 }}>"Не призначено"</span>
            </li>
            <li>Співробітник з'явиться у таблиці</li>
          </ul>
          <p>
            💳 <strong>На наступному кроці</strong> ми розглянемо, як призначити картку
            співробітнику!
          </p>
        </div>
      ),
      placement: 'top',
    },

    // ============================================
    // ЧАСТЬ 2 (продолження): КЕРУВАННЯ КАРТКАМИ (5 шагов)
    // ============================================
    {
      title: 'Управління картками співробітника',
      target: '.card-preview-box',
      screen: 'create',
      content: (
        <div>
          <p>
            <strong>Це панель керування картками.</strong> Тут ви керуєте картками співробітника.
          </p>
          <p>📋 Доступні дії:</p>
          <ul>
            <li>
              <strong>Призначити</strong> — видати картку зі списку доступних
            </li>
            <li>
              <strong>Вилучити</strong> — забрати картку у співробітника (щоб призначити іншому)
            </li>
            <li>
              <strong>Заблокувати </strong> — заблокувати картку співробітника у разі втрати або пошкодження
            </li>
          </ul>
          <p>💡 Статус картки показує її поточний стан у системі.</p>
        </div>
      ),
      placement: 'left',
    },
    {
      title: 'Вибір картки зі списку',
      target: '.card-select-wrapper',
      screen: 'create',
      content: (
        <div>
          <p>
            <strong>Виберіть картку зі списку</strong>, щоб призначити її цьому співробітнику.
          </p>
          <p>📦 У випадаючому списку відображаються:</p>
          <ul>
            <li>
              <span style={{ color: '#1890ff', fontWeight: 600 }}>Випущені картки</span> зі статусом
              "Готова до видачі"
            </li>
            <li>
              <strong>Номер картки</strong> для ідентифікації
            </li>
          </ul>
          <p>
            ⚠️ Якщо список порожній або немає доступних карток — ви можете скористатися кнопкою
            <strong>«Замовити СКД»</strong> у таблиці списку співробітників.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      title: 'Кнопка "Призначити"',
      target: '.contact-assign-btn',
      screen: 'create',
      content: (
        <div>
          <p>
            <strong>Натисніть "Призначити"</strong>, щоб видати вибрану картку співробітнику.
          </p>
          <p>✅ Після призначення:</p>
          <ul>
            <li>
              Статус картки зміниться на{' '}
              <span style={{ color: '#52c41a', fontWeight: 600 }}>"Видана"</span>
            </li>
            <li>Картка буде закріплена за цим співробітником</li>
            <li>Співробітник зможе використовувати картку для доступу</li>
            <li>У таблиці з'явиться номер картки біля імені співробітника</li>
          </ul>
          <p>💡 Після видачі картка зникне зі списку доступних.</p>
        </div>
      ),
      placement: 'right',
    },
    {
      title: 'Кнопка "Вилучити" — переміщення картки',
      target: '.contact-withdraw-btn',
      screen: 'create',
      content: (
        <div>
          <p>
            <strong>"Вилучити"</strong> — тимчасово забрати картку у співробітника.
          </p>
          <p>🔄 Коли використовувати:</p>
          <ul>
            <li>
              <strong>Переміщення картки</strong> від одного співробітника до іншого
            </li>
            <li>Співробітник пішов у відпустку</li>
            <li>Потрібно тимчасово заблокувати доступ</li>
            <li>Перерозподіл карток між співробітниками</li>
          </ul>
          <p>
            ✅ <strong>Картка НЕ видаляється!</strong> Вона повертається у список доступних зі
            статусом <span style={{ color: '#1890ff', fontWeight: 600 }}>"Готова до видачі"</span> і її
            можна знову призначити будь-кому зі співробітників.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      title: 'Кнопка "Заблокувати "',
      target: '.contact-delete-btn',
      screen: 'create',
      content: (
        <div>
          <p style={{ color: '#dc2626', fontWeight: 600, fontSize: '15px' }}>
            ⚠️ БЛОКУВАННЯ КАРТКИ СКД
          </p>
          <p>
            <strong>"Заблокувати "</strong> — тимчасово або назавжди блокує картку співробітника в системі.
          </p>
          <p>🚫 Після блокування:</p>
          <ul>
            <li>
              Картка переходить у статус <strong>Заблокована</strong>
            </li>
            <li>
              Її <strong>неможливо використовувати</strong> для доступу
            </li>
            <li>
              При блокуванні необхідно обов'язково <strong>обрати причину</strong> (втрата, пошкодження тощо)
            </li>
          </ul>
          <p
            style={{
              marginTop: '12px',
              padding: '10px',
              background: '#fef2f2',
              borderRadius: '8px',
              border: '1px solid #fecaca',
            }}
          >
            💡 <strong>Використовуйте, якщо:</strong>
            <br />
            • Співробітник втратив або зламав картку
            <br />
            • Співробітника звільнено (і картка не була вилучена)
          </p>
        </div>
      ),
      placement: 'right',
      customAnchorOffset: 80, // Зміщення anchor вниз на 80px
    },
  ];

  const currentStepData = allSteps[tourStep];
  const totalSteps = allSteps.length;

  // Дополнительная прокрутка к элементу если он вне видимости
  useEffect(() => {
    if (!isReady) return;

    const timer = setTimeout(() => {
      const element = document.querySelector(currentStepData.target);
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const isVisible =
        rect.top >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.left >= 0 &&
        rect.right <= window.innerWidth;

      if (!isVisible) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [tourStep, isReady, currentStepData.target]);

  const handleNext = () => {
    if (!canProceed) return; // Блокируем если не прошло 10 секунд (только для непрочитанных)

    const currentStepData = allSteps[tourStep];

    // Если у текущего шага есть autoNavigate - выполняем навигацию
    if (currentStepData.autoNavigate && tourStep < allSteps.length - 1) {
      // Переходим на нужную страницу
      history.push(currentStepData.autoNavigate);

      // Небольшая задержка перед переходом к следующему шагу
      setTimeout(() => {
        setStep(tourStep + 1);
      }, 500);
    } else if (tourStep < allSteps.length - 1) {
      setStep(tourStep + 1);
    } else {
      // Завершение тура - возвращаемся к таблице и отправляем запрос
      history.push('/contacts');
      finishTour();
      // Вызываем callback для отправки запроса о прочтении инструкций
      onFinish?.();
    }
  };

  const handlePrev = () => {
    if (tourStep > 0) {
      const prevStepNum = tourStep - 1;
      const prevStepData = allSteps[prevStepNum];

      // Если предыдущий шаг на другом экране - переходим туда
      if (prevStepData.screen !== currentScreen) {
        if (prevStepData.screen === 'table') {
          history.push('/contacts');
        } else if (prevStepData.screen === 'create') {
          history.push('/contacts/create');
        }

        setTimeout(() => {
          setStep(prevStepNum);
        }, 300);
      } else {
        setStep(prevStepNum);
      }
    }
  };

  const handleSkip = () => {
    // При пропуске НЕ отправляем запрос о прочтении
    history.push('/contacts');
    stopTour();
  };

  // Находим элемент для текущего шага
  let targetElement = document.querySelector(currentStepData.target);

  // Если элемент не найден, пытаемся найти fallback селектор
  if (!targetElement && currentStepData.fallbackTarget) {
    targetElement = document.querySelector(currentStepData.fallbackTarget);
  }

  // Если элемент не найден - не показываем тур
  if (!targetElement) {
    return null;
  }

  // Если еще не готовы после смены экрана - не показываем
  if (!isReady) {
    return null;
  }

  // Умное определение placement чтобы модалка не перекрывала элемент
  const getSmartPlacement = () => {
    const rect = targetElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const popoverHeight = 400; // Примерная высота модалки
    const popoverWidth = 450; // Примерная ширина модалки

    const requestedPlacement = currentStepData.placement;

    // Проверяем, поместится ли модалка в запрошенной позиции
    switch (requestedPlacement) {
      case 'top':
        if (rect.top > popoverHeight) return 'top';
        if (viewportHeight - rect.bottom > popoverHeight) return 'bottom';
        if (rect.left > popoverWidth) return 'left';
        return 'right';

      case 'bottom':
        if (viewportHeight - rect.bottom > popoverHeight) return 'bottom';
        if (rect.top > popoverHeight) return 'top';
        if (viewportWidth - rect.right > popoverWidth) return 'right';
        return 'left';

      case 'left':
        if (rect.left > popoverWidth) return 'left';
        if (viewportWidth - rect.right > popoverWidth) return 'right';
        if (rect.top > popoverHeight) return 'top';
        return 'bottom';

      case 'right':
        if (viewportWidth - rect.right > popoverWidth) return 'right';
        if (rect.left > popoverWidth) return 'left';
        if (viewportHeight - rect.bottom > popoverHeight) return 'bottom';
        return 'top';

      default:
        return requestedPlacement;
    }
  };

  const smartPlacement = getSmartPlacement();

  return (
    <>
      {/* Затемнение фона */}
      <div className="tour-overlay" onClick={handleSkip} />

      {/* Подсветка текущего элемента */}
      <div className="tour-spotlight" style={getSpotlightStyle(targetElement)} />

      {/* Якорь для Popover - видимая точка */}
      <div
        id="tour-anchor"
        style={{
          ...getAnchorStyle(targetElement, smartPlacement, currentStepData.customAnchorOffset),
          position: 'fixed',
          zIndex: 1002,
        }}
      >
        {/* Popover с инструкцией */}
        <Popover
          visible={true}
          placement={smartPlacement}
          title={
            <div className="tour-popover-title">
              <span>{currentStepData.title}</span>
              <span className="tour-step-counter">
                {tourStep + 1} / {totalSteps}
              </span>
            </div>
          }
          content={
            <div className="tour-popover-content">
              <div className="tour-content-text">{currentStepData.content}</div>

              {/* Прогресс-бар */}
              <div className="tour-progress-bar" style={{ marginTop: '16px' }}>
                <div className="tour-progress-track">
                  <div
                    className="tour-progress-fill"
                    style={{ width: `${((tourStep + 1) / totalSteps) * 100}%` }}
                  />
                </div>
                <div className="tour-progress-text">
                  Крок {tourStep + 1} з {totalSteps}
                </div>
              </div>

              {/* Кнопки навигации */}
              <div className="tour-actions">
                <Button
                  text="Пропустити"
                  bg="orange"
                  onClick={handleSkip}
                  style={{ minWidth: '110px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  {tourStep > 0 && (
                    <Button
                      text="Назад"
                      bg="turquoise"
                      onClick={handlePrev}
                      style={{ minWidth: '110px' }}
                    />
                  )}
                  <div style={{ position: 'relative', minWidth: '110px' }}>
                    {/* Таймер-прогресс всередині кнопки (только если еще не читали) */}
                    {!instructionsRead && !canProceed && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          height: '100%',
                          width: `${((10 - timeLeft) / 10) * 100}%`,
                          background: 'rgba(219, 62, 177, 0.4)',
                          borderRadius: '22px',
                          transition: 'width 0.3s linear',
                          pointerEvents: 'none',
                          zIndex: 0,
                        }}
                      />
                    )}
                    <Button
                      text={tourStep === totalSteps - 1 ? 'Завершити' : 'Далі'}
                      bg="pink"
                      onClick={handleNext}
                      disabled={!canProceed}
                      style={{ minWidth: '110px', position: 'relative', zIndex: 1 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          }
          overlayClassName="tour-popover"
          getPopupContainer={() => document.body}
        >
          <div style={{ width: 0, height: 0 }} />
        </Popover>
      </div>
    </>
  );
};

// Функция для получения позиции и размеров элемента
function getSpotlightStyle(element) {
  const rect = element.getBoundingClientRect();
  // Увеличенные отступы для лучшей видимости
  const padding = 8;
  return {
    position: 'fixed',
    top: `${rect.top - padding}px`,
    left: `${rect.left - padding}px`,
    width: `${rect.width + padding * 2}px`,
    height: `${rect.height + padding * 2}px`,
    zIndex: 1001,
  };
}

function getAnchorStyle(element, placement, customOffset = 0) {
  const rect = element.getBoundingClientRect();
  const padding = 8;

  // Позиционируем якорь на границе элемента в зависимости от placement
  switch (placement) {
    case 'top':
      return {
        top: `${rect.top - padding}px`,
        left: `${rect.left + rect.width / 2}px`,
      };
    case 'bottom':
      return {
        top: `${rect.bottom + padding}px`,
        left: `${rect.left + rect.width / 2}px`,
      };
    case 'left':
      return {
        top: `${rect.top + rect.height / 2 + customOffset}px`,
        left: `${rect.left - padding}px`,
      };
    case 'right':
      return {
        top: `${rect.top + rect.height / 2 + customOffset}px`,
        left: `${rect.right + padding}px`,
      };
    default:
      // Fallback на центр
      return {
        top: `${rect.top + rect.height / 2 + customOffset}px`,
        left: `${rect.left + rect.width / 2}px`,
      };
  }
}

export default ContactsTour;
